import { extendTheme, type ThemeConfig } from '@chakra-ui/react';

const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: false,
};

const fonts = {
  heading: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`,
  body: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`,
  mono: `'SF Mono', 'Fira Code', 'Fira Mono', Menlo, Consolas, 'Courier New', monospace`,
  display: `'Raleway', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`,
};

const colors = {
  brand: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#2563EB',
    600: '#1D4ED8',
    700: '#1e40af',
    800: '#1e3a8a',
    900: '#172554',
  },
  wizard: {
    dark: '#1a1a1a',
    darkSecondary: '#2d2d2d',
    darkBorder: '#374151',
    accent: '#84cc16',
    welcomeBg: '#4a4a4a',
    chipBg: '#f3f4f6',
    chipBgDark: 'transparent',
    chipBorder: '#e5e7eb',
    chipBorderDark: '#374151',
    chipSelected: '#1a1a1a',
    chipSelectedDark: '#ffffff',
    textMuted: '#6b7280',
    textMutedDark: '#9ca3af',
  },
  onboarding: {
    cream: '#fbf5ef',
    creamAlt: '#fffaf9',
    deepPlum: '#270d17',
    plum: '#491231',
    plumSecondary: '#4b1632',
    plumDark: '#281119',
    plumSurface: '#56213d',
    roseDeep: '#642d4c',
    roseMid: '#683550',
    roseWarm: '#814c68',
    roseMuted: '#7a5557',
    mutedRose: '#926f80',
    roseFaint: '#ccbabd',
    placeholder: '#957383',
    pinkAccent: '#d695b7',
    lavender: '#c9cefd',
    warmBeige: '#d0b5a5',
    warmLight: '#f4e2d1',
    peach: '#fbe5d8',
    white: '#ffffff',
    darkBg: '#180018',
    darkBgSubtle: '#280049',
    darkBlob1: '#040203',
    darkBlob2: '#0f0408',
    darkBlob3: '#110411',
    darkBlob4: '#11060c',
    midRose1: '#563347',
    midRose2: '#573447',
  },
};

const styles = {
  global: {
    'html, body': {
      bg: 'white',
      fontFamily: 'body',
      color: 'gray.800',
    },
  },
};

const components = {
  Button: {
    baseStyle: {
      borderRadius: '8px',
      fontWeight: 'semibold',
    },
  },
  Input: {
    defaultProps: {
      focusBorderColor: 'brand.500',
    },
    variants: {
      outline: {
        field: {
          borderRadius: '8px',
        },
      },
    },
  },
  Card: {
    baseStyle: {
      container: {
        borderRadius: '8px',
        boxShadow: 'sm',
        bg: 'white',
      },
    },
  },
};

// Landing display type scale (Figma 35/40/55px) — fluid via clamp() so headings
// are exact at 1440 and reflow on small viewports. Accessed as fontSize="display.lg".
const fontSizes = {
  display: {
    sm: 'clamp(1.75rem, 1.1rem + 2.6vw, 2.1875rem)',
    md: 'clamp(2rem, 1.2rem + 3.2vw, 2.5rem)',
    lg: 'clamp(2.25rem, 1rem + 5vw, 3.4375rem)',
  },
};

// Landing radii (Figma cards 20–30px, pill buttons). borderRadius="card" / "pill".
const radii = {
  card: '24px',
  pill: '9999px',
};

const theme = extendTheme({
  config,
  fonts,
  fontSizes,
  colors,
  radii,
  styles,
  components,
});

export default theme;
